// ----------------------------------------------------------------------
// <copyright file="Izenda.FieldContainerAction.jsx" company="Izenda">
// Copyright (c) 2015 Izenda, Inc.  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and  International
// Copyright Laws. Unauthorized reproduction,  reverse-engineering, and
// distribution of all or any portion of  the code contained in this file is
// strictly prohibited and may  result in severe civil and criminal penalties
// and will be  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES  ARE CONFIDENTIAL AND
// PROPRIETARY TRADE  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
// LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET  CONTROLS AS
// PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED  FILES OR ANY
// PORTION OF ITS CONTENTS SHALL AT NO TIME BE  COPIED, TRANSFERRED, SOLD,
// DISTRIBUTED, OR OTHERWISE MADE  AVAILABLE TO OTHER INDIVIDUALS WITHOUT
// EXPRESS WRITTEN CONSENT  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON  ADDITIONAL
// RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * FieldContainer actions
 */
import { compose, withHandlers, withState, onlyUpdateForKeys } from 'recompose';
import { action } from 'mobx';
import { findDOMNode } from 'react-dom';
import { filter, isEmpty, map, isNil, includes, get, curry, forEach, curryN } from 'lodash/fp';
import AppGlobal from 'Izenda.AppGlobal';
import droppable from 'share/components/hoc/Izenda.Droppable';
import ReportBodyConstants from 'logic/data/Izenda.ReportBodyConstants';
import ReportDesignerConstants from 'logic/data/Izenda.ReportDesignerConstants';
import SelectionFieldPopup from './Izenda.SelectionFieldPopup';
import { isBinary } from 'share/utils/Izenda.ObjectUtils';
import { ContainerName } from 'logic/data/Izenda.ReportPartChartConstants';

const validateAddingFieldToDefault = (props, fieldCollection, isCheckRunningField) => {
  const errorField = filter(
    field =>
      isCheckRunningField
        ? isInvalidRunningField(props.reportPart, props.container, field.isRunningField)
        : isBinary(field.izendaDataType || field.fieldDataType),
    fieldCollection
  );

  if (isEmpty(errorField)) {
    return {
      message: null,
      errorField: null
    };
  }
  const errorMessage = isCheckRunningField
    ? String.format(ReportBodyConstants.MESSAGES.M28, map('name', errorField).join(', '))
    : String.format(ReportBodyConstants.MESSAGES.M05, map('name', errorField).join(', '));
  return {
    message: errorMessage,
    errorField
  };
};

const validateAddNewFieldToContainer = props => fieldCollection => {
  const exceedMaximumField =
    !!props.maximumField && props.container.elements.length + fieldCollection.length > props.maximumField;

  if (exceedMaximumField) {
    return {
      message: ReportDesignerConstants.BUNDLE.MESSAGE.FIELDS_EXCEEDED_LIMITATION,
      errorField: fieldCollection
    };
  }
  const errorRunningField = props.validateAddingFieldToDefault(fieldCollection, true);

  if (!isNil(errorRunningField.errorField)) return errorRunningField;

  const reportPartContent = props.reportPart.reportPartContent;

  if (reportPartContent.validateCanAddField) {
    return reportPartContent.validateCanAddField(props.store.masterModel, props.container, fieldCollection);
  }
  return props.validateAddingFieldToDefault(fieldCollection, false);
};

/**
 * This function is to show the confirm message to disable multi color in case of there are more than one metric.
 * @param {Object} props are properties of object
 */
const confirmTurnOffMultiColor = props => {
  return () => {
    const reportPartContent = get('reportPart.reportPartContent', props);
    const validateAddMoreMetricMessage = reportPartContent.validateMoreThanOneMetric
      ? reportPartContent.validateMoreThanOneMetric(props.container)
      : null;

    if (validateAddMoreMetricMessage) {
      return props.confirmFn('Confirmation', {
        description: validateAddMoreMetricMessage
      });
    }
    return Promise.resolve();
  };
};

/**
 * Confirm remove regression line setting when add separator
 * @param {Object} props are properties of object
 */
const confirmRemoveRegressionLine = props => () => {
  const {
    reportPart: { reportPartContent },
    container
  } = props;
  const removeRegressionLine = reportPartContent.validateRemoveRegressionLine
    ? reportPartContent.validateRemoveRegressionLine(container)
    : { remove: false };

  return removeRegressionLine.remove
    ? removeRegressionLine.message
      ? props
          .confirmFn('Confirmation', {
            description: removeRegressionLine.message
          })
          .then(reportPartContent.resetRegressionLineDefaultValue.bind(reportPartContent))
      : Promise.resolve(reportPartContent.resetRegressionLineDefaultValue())
    : Promise.resolve();
};

const isInvalidRunningField = (reportPart, container, isRunningField) => {
  if (!isRunningField) {
    return false;
  }
  const type = ReportDesignerConstants.REPORT_PART_TYPE;
  let containerType = {};
  switch (reportPart.type) {
    case type.Chart:
      containerType = { name: 'Chart', key: 'chartType' };
      break;
    case type.Gauge:
      containerType = { name: 'Gauge', key: 'reportPartStyle' };
      break;
    case type.Map:
      containerType = { name: 'Map', key: 'chartType' };
      break;
    case type.Grid:
      containerType = { name: 'Grid', key: 'gridStyle' };
      break;
  }
  const typeReport = reportPart.reportPartContent[containerType.key];
  return includes(
    container.name,
    ReportBodyConstants.RUNNING_FUNCTION_INVALID_CONTAINER[containerType.name][typeReport]
  );
};

const validateAddNewFieldToReportPart = props => fieldCollection => {
  const fieldLimitValue = AppGlobal.advancedSettings.performance.fieldLimitValue;

  if (props.reportPart.fieldCollection.length + fieldCollection.length > fieldLimitValue) {
    return {
      message: ReportDesignerConstants.BUNDLE.MESSAGE.FIELDS_EXCEEDED_LIMITATION,
      errorField: fieldCollection
    };
  }

  return props.validateAddNewFieldToContainer(fieldCollection);
};

const warning = (props, errorMessage) =>
  props.confirmFn('Information', {
    description: errorMessage,
    type: 'error',
    showCloseIndicator: true
  });

const changePositionField = action((props, field, position, dragItemContainer) => {
  let store = props.store;
  let reportPart = props.reportPart;

  reportPart.changePositionField(field, position, dragItemContainer);
  reportPart.shouldUpdatePreview && store.needUpdatePreview();
});

const changeFieldContainer = action((props, movingItem, field, position, dragItemContainer, container) => {
  const isRunningField = get('field.isRunningField', field);

  if (isInvalidRunningField(props.reportPart, container, isRunningField)) {
    props.confirmMessage('error', String.format(ReportBodyConstants.MESSAGES.M28, field.name));
    return;
  }

  const store = props.store;
  const reportPart = props.reportPart;

  store.reportFilters.removeAggregatedField(field);

  movingItem.onDeleteElement && movingItem.onDeleteElement(field);
  return reportPart
    .changeFieldContainer(field, position, dragItemContainer, container)
    .then(addedField => props.onAddElement && props.onAddElement(addedField))
    .then(() => {
      reportPart.shouldUpdatePreview && store.needUpdatePreview();
    });
});

const addField = props => (field, querySource, position) => {
  const {
    container,
    reportPart,
    reportPart: { reportPartContent: content },
    onAddElement
  } = props;

  return reportPart
    .insertFieldToContainer(container, field, querySource, position)
    .then(addedField => {
      onAddElement && onAddElement(addedField);
      return addedField;
    })
    .then(addedField => {
      content.afterAddField && content.afterAddField();
      // This below approach is required for re-rendering (Combination chart)
      return Promise.resolve().return(addedField);
    });
};

const openSelectionFieldPopup = props => () => props.openPopup(SelectionFieldPopup, props);

const droppableAction = droppable(
  [ReportDesignerConstants.GENNERAL.FIELD_ITEM, ReportBodyConstants.DRAG_ITEM.REPORT_PART_FIELD_ITEM],
  {
    drop(props, monitor) {
      if (monitor.didDrop()) {
        return;
      }

      const item = monitor.getItem() || {};
      const field = item.querySourceField || {};
      const dragItemContainer = item.dragItemContainer;
      const dropPosition = props.position;
      const reportPart = props.reportPart;

      if (
        item.itemType !== ReportDesignerConstants.GENNERAL.FIELD_ITEM &&
        item.itemType !== ReportBodyConstants.DRAG_ITEM.REPORT_PART_FIELD_ITEM
      ) {
        return;
      }

      if (field.isRunningField) {
        if (isInvalidRunningField(reportPart, props.container, field.isRunningField)) {
          props.warning(String.format(ReportBodyConstants.MESSAGES.M28, field.name));
          return;
        }
      }

      if (dragItemContainer === props.container) {
        props.changePositionField(field, dropPosition, dragItemContainer);
        return;
      }

      let warningPromise = Promise.resolve();
      if (dragItemContainer && dragItemContainer !== props.container) {
        const confirmMessagePromise =
          props.container.name === ContainerName.SEPARATORS
            ? props.confirmRemoveRegressionLine()
            : props.confirmTurnOffMultiColor();
        // changeFieldContainer
        confirmMessagePromise.then(() => {
          const validateChangeContainer = props.validateAddNewFieldToContainer([field]);
          if (validateChangeContainer.message) {
            warningPromise = props.warning(validateChangeContainer.message);
          }

          warningPromise.then(() => {
            !includes(field, validateChangeContainer.errorField) &&
              props.changeFieldContainer(item, field, dropPosition, dragItemContainer, props.container);
          });
        });
        return;
      }

      const validateAddNewField = props.validateAddNewFieldToReportPart([field]);

      warningPromise = !isEmpty(validateAddNewField.message)
        ? props.warning(validateAddNewField.message)
        : props.confirmTurnOffMultiColor();

      if (props.container.name === ContainerName.SEPARATORS) {
        warningPromise = props.confirmRemoveRegressionLine();
      }

      warningPromise.then(() => {
        const store = props.store;
        !includes(field, validateAddNewField.errorField) &&
          props.addField(item.querySourceField, item.dataObject, dropPosition).then(() => {
            reportPart.shouldUpdatePreview && store.needUpdatePreview();
          });
      });
    },

    canAccess(props, currentDragItem) {
      const dragItem = currentDragItem || {};
      const item = dragItem.querySourceField || {};

      const isRunningField = get('isRunningField', item) || get('field.isRunningField', item);

      if (
        dragItem.itemType !== ReportDesignerConstants.GENNERAL.FIELD_ITEM &&
        dragItem.itemType !== ReportBodyConstants.DRAG_ITEM.REPORT_PART_FIELD_ITEM
      ) {
        return false;
      }

      if (!isRunningField && dragItem.dragItemContainer === props.container) {
        return true;
      }

      const validateResult =
        dragItem.itemType === ReportDesignerConstants.GENNERAL.FIELD_ITEM
          ? props.validateAddNewFieldToReportPart([item])
          : props.validateAddNewFieldToContainer([item]);

      return (
        !includes(item, validateResult.errorField) &&
        !isInvalidRunningField(props.reportPart, props.container, isRunningField)
      );
    },

    hover(props, monitor, component) {
      const clientOffset = monitor.getClientOffset();
      const componentRect = findDOMNode(component).getBoundingClientRect();
      const listItem = findDOMNode(component).getElementsByClassName('field-wrapper');
      const posItemX = clientOffset.x - componentRect.left;
      const posItemY = clientOffset.y - componentRect.top;
      let exist = false;
      const elements = props.container.elements;

      if (posItemX > 0 && posItemY > 0 && posItemX < componentRect.width && posItemY < componentRect.height) {
        forEach(element => {
          if (
            element.offset.left <= clientOffset.x &&
            clientOffset.x <= element.offset.left + element.offset.width &&
            element.offset.top <= clientOffset.y &&
            clientOffset.y <= element.offset.top + element.offset.height
          ) {
            resetStyleItem();
            props.setPostition(element.position);
            setStyleForItem(element.position - 1, 'Left');
            exist = true;
          }
        }, elements);

        if (!exist && elements.length > 0) {
          resetStyleItem();
          props.setPostition(elements.length + 1);
          setStyleForItem(elements.length - 1, 'Right');
        }

        if (!elements.length) {
          props.setPostition(1);
        }
      }

      function resetStyleItem() {
        forEach(item => {
          item.style.borderLeftStyle = '';
          item.style.borderRightStyle = '';
        }, listItem);
      }

      function setStyleForItem(itemPosition, type) {
        if (isNil(listItem[itemPosition])) {
          return;
        }
        listItem[itemPosition].style[`border${type}Style`] = ReportDesignerConstants.GENNERAL.BORDER_HOVER_ITEM.STYLE;
        listItem[itemPosition].style[`border${type}Color`] = ReportDesignerConstants.GENNERAL.BORDER_HOVER_ITEM.COLOR;
      }
    }
  }
);

export default compose(
  withState('position', 'setPostition', 1),
  withHandlers({
    changePositionField: curryN(4, changePositionField),
    changeFieldContainer: curryN(6, changeFieldContainer),
    validateAddingFieldToDefault: curry(validateAddingFieldToDefault),
    addField,
    /**
     * Show warning popup
     */
    warning: curry(warning)
  }),
  withHandlers({
    validateAddNewFieldToContainer
  }),
  withHandlers({
    confirmTurnOffMultiColor,
    confirmRemoveRegressionLine
  }),
  withHandlers({
    validateAddNewFieldToReportPart
  }),
  withHandlers({
    /**
     * Show popup to select fields into container.
     * @return {Promise} Popup promise
     */
    openSelectionFieldPopup
  }),
  droppableAction,
  onlyUpdateForKeys(['isOver', 'canDrop', 'container'])
);



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/FieldContainers/Izenda.FieldContainerAction.js